import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { Em } from "./kit";
import VideoBg from "./VideoBg";
import { PlaceProvider } from "./place";
import { MegaPanel, MegaTrigger, MobileOutcomes } from "./MegaMenu";
import { OUTCOMES } from "./siteNav";

const NAV = [["Product", "/product"], ["Solutions", "/solutions"], ["Industries", "/industries"], ["Pricing", "/pricing"], ["Resources", "/resources"], ["About", "/about"]];

export function useTitle(title, description) {
  useEffect(() => {
    document.title = title ? `${title} — TRAIDE` : "TRAIDE — The operating system for trade businesses";
    if (description) document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    window.scrollTo(0, 0);
  }, [title, description]);
}

export default function SiteShell({ children, title, description }) {
  useTitle(title, description);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const hoverOpened = useRef(false);
  const openByHover = () => { hoverOpened.current = true; setMega(true); };
  const toggleByClick = () => { if (hoverOpened.current && mega) { hoverOpened.current = false; return; } hoverOpened.current = false; setMega((m) => !m); };
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);
  useEffect(() => { const f = () => setScrolled(window.scrollY > 24); f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  useEffect(() => { setMega(false); setOpen(false); }, [location.pathname, location.search, location.hash]);
  useEffect(() => { if (!mega) return; const k = (e) => e.key === "Escape" && setMega(false); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [mega]);
  return (
    <PlaceProvider>
    <div className="site min-h-screen" data-testid="site">
      <header onMouseLeave={() => setMega(false)} className={`sticky top-0 z-40 transition-[background-color,border-color] duration-300 ${mega ? "border-b border-white/10 bg-[#0A0A0B]" : scrolled ? "border-b border-white/10 bg-[#0A0A0B]/85 backdrop-blur-md" : "border-b border-transparent bg-transparent"}`} data-testid="site-nav">
        <div className="wrap flex h-14 items-center">
          <Link to="/" className="flex items-center pr-8" data-testid="site-logo"><img src="/logo.webp" alt="TRAIDE" className="h-9 w-auto" /></Link>
          <nav className="hidden items-center gap-7 lg:flex">
            <MegaTrigger open={mega} onToggle={toggleByClick} onOpen={openByHover} active={location.pathname === "/product"} />
            {NAV.slice(1).map(([l, to]) => <NavLink key={to} to={to} onMouseEnter={() => setMega(false)} className={({ isActive }) => `text-[13px] font-medium transition-colors ${isActive ? "text-white" : "text-zinc-400 hover:text-white"}`} data-testid={`site-nav-${l.toLowerCase()}`}>{l}</NavLink>)}
          </nav>
          <div className="ml-auto flex items-center gap-6">
            <span className="hidden sm:block"><Link to="/login" className="text-[13px] font-medium text-zinc-300 transition-colors hover:text-white" data-testid="nav-login-btn">Login</Link></span>
            <Link to="/book-demo" className="site-btn-amber !px-4 !py-2" data-testid="nav-book-demo-btn">Book a demo</Link>
            <button className="inline-flex h-9 w-9 items-center justify-center border border-white/15 text-white lg:hidden" onClick={() => setOpen(true)} aria-label="Menu" data-testid="site-menu-open"><Menu size={17} /></button>
          </div>
        </div>
        {mega && <MegaPanel onClose={() => setMega(false)} />}
      </header>
      {open && (
        <div className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-[#0A0A0B] p-5 lg:hidden" data-testid="site-mobile-menu">
          <div className="flex h-9 items-center justify-between"><img src="/logo.webp" alt="TRAIDE" className="h-9 w-auto" /><button className="inline-flex h-9 w-9 items-center justify-center border border-white/15 text-white" onClick={() => setOpen(false)} aria-label="Close" data-testid="site-menu-close"><X size={17} /></button></div>
          <nav className="mt-10 flex flex-col">
            {NAV.map(([l, to], i) => (
              <div key={to}>
                <Link to={to} onClick={() => setOpen(false)} className={`display flex items-center justify-between border-t border-white/10 py-4 text-[28px] text-white ${i === NAV.length - 1 ? "border-b" : ""}`} data-testid={`site-mnav-${l.toLowerCase()}`}><span className="flex items-baseline gap-4"><span className="mono-label">0{i + 1}</span>{l}</span><ArrowRight size={18} className="text-zinc-600" /></Link>
                {l === "Product" && <MobileOutcomes onClose={() => setOpen(false)} />}
              </div>
            ))}
          </nav>
          <div className="mt-10 grid gap-3"><Link to="/book-demo" onClick={() => setOpen(false)} className="site-btn-amber w-full" data-testid="mnav-book-demo">Book a demo</Link><Link to="/login" onClick={() => setOpen(false)} className="site-btn-ghost w-full" data-testid="mnav-login">Login</Link></div>
        </div>
      )}
      <main>{children}</main>
      <Footer />
    </div>
    </PlaceProvider>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10" data-testid="site-footer">
      <div className="wrap">
        <div className="grid gap-10 py-14 lg:grid-cols-12 lg:py-20">
          <div className="lg:col-span-5">
            <img src="/logo.webp" alt="TRAIDE" className="h-9 w-auto" />
            <p className="mt-6 max-w-sm text-[14px] leading-relaxed text-zinc-400">The operating system for trade businesses. One record for leads, quotes, jobs, projects, staff, customers, supplies, payments and profit.</p>
            <p className="mono-label mt-10 !text-amber-500" data-testid="footer-tagline">TRADE + AI = TRAIDE</p>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:col-span-7">
            {[["Explore", [...OUTCOMES.map((o) => [o.label, o.anchor]), ["All 14 modules", "/product"]]],
              ["Company", [["Solutions", "/solutions"], ["Industries", "/industries"], ["Pricing", "/pricing"], ["Resources", "/resources"], ["About", "/about"], ["Book a demo", "/book-demo"]]],
              ["Access", [["Login", "/login"], ["Contact us", "/contact"], ["Forgot password", "/forgot-password"]]]].map(([h, links]) => (
              <div key={h}>
                <div className="mono-label">{h}</div>
                <ul className="mt-4 space-y-2.5">{links.map(([l, to]) => <li key={to}><Link to={to} className="text-[13px] text-zinc-300 transition-colors hover:text-white">{l}</Link></li>)}</ul>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 py-5 text-[12px] text-zinc-500"><span>© {new Date().getFullYear()} TRAIDE. All rights reserved.</span><span className="mono-label !text-[10px]">Admin · Staff · Client — one connected platform</span></div>
      </div>
    </footer>
  );
}

export function Section({ children, className = "", paper = false, id, testid, frame = false, inner = "" }) {
  return <section id={id} className={`${paper ? "site-paper" : ""} ${className}`} data-testid={testid}><div className={`${frame ? "frame" : ""} wrap ${inner}`}>{children}</div></section>;
}

/* Title supports *emphasis* → editorial serif italic */
export function rich(title) {
  if (typeof title !== "string" || !title.includes("*")) return title;
  return title.split("*").map((part, i) => (i % 2 ? <Em key={i}>{part}</Em> : part));
}

export function Headline({ index, eyebrow, title, copy, paper = false, size = "md", className = "" }) {
  const sizes = { xl: "text-[40px] sm:text-6xl lg:text-[76px]", lg: "text-[34px] sm:text-5xl lg:text-[58px]", md: "text-[28px] sm:text-[36px] lg:text-[42px]", sm: "text-2xl sm:text-3xl" };
  return (
    <div className={`max-w-3xl ${className}`}>
      {(eyebrow || index) && <div className="flex items-center gap-3"><span className="mono-label amber">{index}</span>{index && eyebrow && <span className="h-px w-6 bg-amber-500/60" />}<span className="mono-label">{eyebrow}</span></div>}
      <h2 className={`display mt-5 leading-[0.98] ${sizes[size]} ${paper ? "text-zinc-950" : "text-white"}`}>{rich(title)}</h2>
      {copy && <p className={`mt-6 max-w-xl text-[15px] leading-relaxed sm:text-[17px] ${paper ? "text-zinc-600" : "text-zinc-400"}`}>{copy}</p>}
    </div>
  );
}

export function CtaRow({ paper = false, primary = ["Book a demo", "/book-demo"], secondary = ["Start free trial", "/register"], testid = "cta" }) {
  const Sec = secondary && secondary[1].startsWith("#") ? "a" : Link;
  return (
    <div className="flex flex-wrap items-center gap-6">
      <Link to={primary[1]} className={paper ? "site-btn-dark" : "site-btn-amber"} data-testid={`${testid}-primary`}>{primary[0]} <ArrowRight size={15} /></Link>
      {secondary && <Sec {...(Sec === "a" ? { href: secondary[1] } : { to: secondary[1] })} className="site-link" data-testid={`${testid}-secondary`}>{secondary[0]} <ArrowRight size={14} /></Sec>}
    </div>
  );
}

export function FinalCta({ video = false }) {
  return (
    <section className="relative border-t border-white/10" data-testid="final-cta">
      {video && <VideoBg name="cta" opacity={0.5} overlay="bg-[#0A0A0B]/55" position="center 30%" />}
      <div className="wrap relative z-10 grid lg:grid-cols-12">
        <div className="py-20 lg:col-span-8 lg:border-r lg:border-white/10 lg:py-32 lg:pr-16">
          <span className="mono-label amber">See it with your numbers</span>
          <h2 className="display mt-5 text-[40px] leading-[0.96] text-white sm:text-6xl lg:text-[80px]">Run your trade business<br />from <Em>one place.</Em></h2>
          <p className="mt-8 max-w-lg text-[16px] leading-relaxed text-zinc-300">A 30-minute walkthrough with your trade, your job types and your team size. We drive the real product — no slides.</p>
          <div className="mt-10"><CtaRow testid="final" /></div>
        </div>
        <div className="hidden flex-col justify-end gap-8 py-32 lg:col-span-4 lg:flex lg:pl-12">
          {[["30 min", "Live walkthrough"], ["14 days", "Free trial, no card"], ["1 link", "Your customers' portal"]].map(([v, l]) => <div key={l} className="border-t border-white/15 pt-4"><div className="display text-4xl text-white">{v}</div><div className="mono-label mt-1.5 !text-zinc-300">{l}</div></div>)}
        </div>
      </div>
    </section>
  );
}
